// src/components/DynamicForm.tsx

import React, { useState } from "react";
import {
    CategoryParameter,
    ParameterDataType,
    SelectionType,
} from "../types/Category";
import { Unit } from "../types/Unit";
import { DateComparison, SearchFilter, SearchParameter, SelectableMatchMode } from "../types/SearchFilter";
import { DATE_FORMAT_HINT } from "../utils/dateFormat";
import MultiSelectDropdown from "./filters/MultiSelectDropdown";

interface DynamicFormProps {
    parameters: CategoryParameter[];
    categoryId: string;
    units?: Unit[];
    onSubmit: (searchFilter: SearchFilter) => void;
    /** Optional id so an external submit button can trigger this form. */
    formId?: string;
    /** Hide the built-in submit button (e.g. when an external Search bar submits the form). */
    hideSubmit?: boolean;
}

const DynamicForm: React.FC<DynamicFormProps> = ({
                                                     parameters,
                                                     categoryId,
                                                     units = [],
                                                     onSubmit,
                                                     formId,
                                                     hideSubmit = false,
                                                 }) => {
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [useRange, setUseRange] = useState<Record<string, boolean>>({});

    // Default unit for a DOUBLE param: prefer USD (currency) when present,
    // otherwise the first allowed unit.
    const defaultUnit = (allowedUnits: string[]): string | undefined => {
        const usd = allowedUnits.find(id => units.find(u => u.id === id)?.name?.toUpperCase() === "USD");
        return usd ?? allowedUnits[0];
    };

    // Allowed units for a DOUBLE param. Currency params (non-convertible dimension)
    // aren't pinned to specific currencies — offer every currency in the dimension,
    // resolved live from the units list so newly added currencies appear too.
    const allowedUnitsFor = (param: any): string[] => {
        const raw: string[] = param.unitIds ?? (param.unitId ? [param.unitId] : []);
        if (param.convertible === false && raw[0]) {
            const dimId = units.find(u => u.id === raw[0])?.unitDimensionId;
            if (dimId) return units.filter(u => u.unitDimensionId === dimId).map(u => u.id);
        }
        return raw;
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
        paramName: string
    ) => {
        setFormData({
            ...formData,
            [paramName]: e.target.value,
        });
    };

    const handleRangeChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        paramName: string,
        type: "minimum" | "maximum"
    ) => {
        setFormData((prev) => ({
            ...prev,
            [paramName]: {
                ...(prev[paramName] ?? {}),
                [type]: e.target.value,
            },
        }));
    };

    const toggleRange = (paramName: string) => {
        setUseRange((prev) => ({
            ...prev,
            [paramName]: !prev[paramName],
        }));
        setFormData((prev) => ({
            ...prev,
            [paramName]: {},
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const searchParameters: SearchParameter[] = [];

        parameters.forEach((param) => {
            const value = formData[param.name];
            if (value === undefined || value === "" || value?.length === 0) return;

            // DOUBLE search must carry the unit its value is expressed in;
            // INTEGER has no convertible units, so it carries none.
            const isDouble = param.dataType === ParameterDataType.DOUBLE;
            const allowedUnits = allowedUnitsFor(param);
            const selectedUnit = formData[`__unit_${param.name}`] || defaultUnit(allowedUnits);
            const strictUnit = formData[`__strict_${param.name}`] === true
                || formData[`__strict_${param.name}`] === "true";
            const doubleUnitFields = isDouble ? { unit: selectedUnit, strictUnit } : {};

            // 🔹 RANGE (INTEGER / DOUBLE)
            if (
                useRange[param.name] &&
                (param.dataType === ParameterDataType.INTEGER ||
                    param.dataType === ParameterDataType.DOUBLE)
            ) {
                const min = value.minimum ? Number(value.minimum) : undefined;
                const max = value.maximum ? Number(value.maximum) : undefined;

                if (min === undefined && max === undefined) return;

                searchParameters.push({
                    id: param.id,
                    dataType:
                        param.dataType === ParameterDataType.INTEGER
                            ? "RANGE_INTEGER"
                            : "RANGE_DOUBLE",
                    value: { minimum: min, maximum: max },
                    ...doubleUnitFields,
                });
                return;
            }

            // 🔹 SELECTABLE
            if (param.dataType === ParameterDataType.SELECTABLE) {
                const st = param.selectionType ?? SelectionType.SINGLE;

                // normalize value — always an array. SINGLE means the listing
                // holds one value, but the searcher may accept several (e.g.
                // House Type: condo OR town house), matched as any-of.
                let normalizedValue: any;

                if (st === SelectionType.MULTI) {
                    normalizedValue = Array.isArray(value) ? value : [];
                    if (normalizedValue.length === 0) return;
                } else {
                    normalizedValue = Array.isArray(value)
                        ? value
                        : value && String(value).trim() !== "" ? [String(value)] : [];
                    if (normalizedValue.length === 0) return;
                }

                searchParameters.push({
                    id: param.id,
                    dataType: ParameterDataType.SELECTABLE,
                    selectionType: st,        // ✅ THIS IS THE FIX
                    value: normalizedValue,   // ✅ always array
                    ...(st === SelectionType.MULTI
                        ? { matchMode: (formData[`__match_${param.name}`] as SelectableMatchMode) ?? "ANY" }
                        : {}),
                });

                return;
            }

            // 🔹 DATE (on / before / after / between)
            if (param.dataType === ParameterDataType.DATE) {
                const mode = (formData[`__datemode_${param.name}`] as DateComparison) || "ON";
                const date2 = formData[`__date2_${param.name}`];
                searchParameters.push({
                    id: param.id,
                    dataType: ParameterDataType.DATE,
                    mode,
                    value: String(value),
                    ...(mode === "BETWEEN" && date2 ? { date2: String(date2) } : {}),
                });
                return;
            }

            // 🔹 DEFAULT (STRING / BOOLEAN / EXACT NUMBER)
            searchParameters.push({
                id: param.id,
                dataType: param.dataType,
                value: String(value),
                ...doubleUnitFields,
            });
        });

        onSubmit({
            categoryId,
            searchParameters,
        });
    };

    const renderInput = (param: CategoryParameter) => {
        // 🔹 DATE — comparison mode + native date input(s)
        if (param.dataType === ParameterDataType.DATE) {
            const mode = (formData[`__datemode_${param.name}`] as string) || "ON";
            // Native date inputs default to a small font; match the dropdown's text size.
            const dateInputStyle: React.CSSProperties = {
                width: "13rem",
                padding: "0.4rem",
                fontSize: "1rem",
                fontFamily: "inherit",
            };
            return (
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                    <select
                        value={mode}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [`__datemode_${param.name}`]: e.target.value }))
                        }
                        style={{ width: "8.5rem", flex: "0 0 auto" }}
                    >
                        <option value="ON">On</option>
                        <option value="BEFORE">On or before</option>
                        <option value="AFTER">On or after</option>
                        <option value="BETWEEN">Between</option>
                    </select>
                    {/* Dates stack vertically so BETWEEN's two pickers align. */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                        <input
                            type="date"
                            value={formData[param.name] ?? ""}
                            onChange={(e) => handleInputChange(e, param.name)}
                            style={dateInputStyle}
                        />
                        {mode === "BETWEEN" && (
                            <>
                                <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>and</span>
                                <input
                                    type="date"
                                    value={formData[`__date2_${param.name}`] ?? ""}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, [`__date2_${param.name}`]: e.target.value }))
                                    }
                                    style={dateInputStyle}
                                />
                            </>
                        )}
                    </div>
                    <small style={{ width: "100%", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                        <div>Date format: {DATE_FORMAT_HINT}</div>
                        <div>Selected date{mode === "BETWEEN" ? "s are" : " is"} included in the results.</div>
                    </small>
                </div>
            );
        }

        // 🔹 INTEGER / DOUBLE
        if (
            param.dataType === ParameterDataType.INTEGER ||
            param.dataType === ParameterDataType.DOUBLE
        ) {
            return (
                <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", width: "100%" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <input
                                type="radio"
                                checked={!useRange[param.name]}
                                onChange={() => toggleRange(param.name)}
                            />
                            Exact
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <input
                                type="radio"
                                checked={useRange[param.name]}
                                onChange={() => toggleRange(param.name)}
                            />
                            Range
                        </label>
                    </div>

                    {useRange[param.name] ? (
                        <>
                            <input
                                type="number"
                                placeholder="Min"
                                step={param.dataType === ParameterDataType.DOUBLE ? "any" : "1"}
                                onChange={(e) =>
                                    handleRangeChange(e, param.name, "minimum")
                                }
                            />
                            <input
                                type="number"
                                placeholder="Max"
                                step={param.dataType === ParameterDataType.DOUBLE ? "any" : "1"}
                                onChange={(e) =>
                                    handleRangeChange(e, param.name, "maximum")
                                }
                            />
                        </>
                    ) : (
                        <input
                            type="number"
                            placeholder={param.description}
                            step={param.dataType === ParameterDataType.DOUBLE ? "any" : "1"}
                            onChange={(e) => handleInputChange(e, param.name)}
                        />
                    )}
                </>
            );
        }

        // 🔹 BOOLEAN
        if (param.dataType === ParameterDataType.BOOLEAN) {
            return (
                <select
                    value={formData[param.name] ?? ""}
                    onChange={(e) => handleInputChange(e, param.name)}
                >
                    <option value="">-- no filter --</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                </select>
            );
        }

        // 🔹 SELECTABLE
        if (param.dataType === ParameterDataType.SELECTABLE) {
            const options = param.options ?? [];

            if (param.selectionType === SelectionType.SINGLE) {
                // A listing holds ONE of these values, but a searcher may
                // accept several ("condo or town house") — matched as any-of,
                // so no Any/All choice is needed.
                const selectedSingle: string[] = Array.isArray(formData[param.name])
                    ? formData[param.name]
                    : formData[param.name] ? [String(formData[param.name])] : [];
                return (
                    <div>
                        <MultiSelectDropdown
                            options={options}
                            values={selectedSingle}
                            onChange={(next) =>
                                setFormData({
                                    ...formData,
                                    [param.name]: next,
                                })
                            }
                        />
                        <div className="pf-hint" style={{ color: "var(--text-secondary)", marginTop: "0.3rem" }}>
                            Each listing has just one of these — select every choice you
                            would accept, and listings with any of them will match.
                        </div>
                    </div>
                );
            }

            // MULTI
            const selected: string[] = formData[param.name] ?? [];

            return (
                <div>
                    <MultiSelectDropdown
                        options={options}
                        values={selected}
                        onChange={(next) =>
                            setFormData({
                                ...formData,
                                [param.name]: next,
                            })
                        }
                    />
                    <div className="pf-hint" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.35rem", marginTop: "0.3rem" }}>
                        <span style={{ color: "var(--text-secondary)" }}>
                            A listing can have several of these. Show listings that have:
                        </span>
                        {(["ANY", "ALL"] as const).map((mode) => (
                            <label key={mode} style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontWeight: 400, cursor: "pointer", margin: 0 }}>
                                <input
                                    type="radio"
                                    name={`__match_${param.name}`}
                                    checked={((formData[`__match_${param.name}`] as string) ?? "ANY") === mode}
                                    onChange={() =>
                                        setFormData({
                                            ...formData,
                                            [`__match_${param.name}`]: mode,
                                        })
                                    }
                                />
                                {mode === "ANY" ? "any of the selected choices" : "all of the selected choices"}
                            </label>
                        ))}
                    </div>
                </div>
            );
        }

        // 🔹 STRING (default)
        return (
            <input
                type="text"
                placeholder={param.description}
                value={formData[param.name] ?? ""}
                onChange={(e) => handleInputChange(e, param.name)}
            />
        );
    };

    return (
        <form id={formId} onSubmit={handleSubmit} className="param-filters">
            {parameters.map((param) => {
                const isNumeric = param.dataType === ParameterDataType.INTEGER || param.dataType === ParameterDataType.DOUBLE;
                const isDouble = param.dataType === ParameterDataType.DOUBLE;
                const allowedUnits = allowedUnitsFor(param);
                const selectedUnit = formData[`__unit_${param.name}`] || defaultUnit(allowedUnits);
                const labelUnitId = isDouble ? selectedUnit : param.unitId;
                const unitLabel = isNumeric && labelUnitId
                    ? (units.find(u => u.id === labelUnitId)?.name ?? labelUnitId)
                    : null;
                return (
                    <div key={param.name} className="pf-row">
                        <label className="pf-label">
                            {param.name}
                            {unitLabel && (
                                <span style={{ fontWeight: 400, fontSize: "0.85rem", color: "var(--text-secondary)", marginLeft: "0.4rem" }}>
                                    ({unitLabel})
                                </span>
                            )}
                        </label>
                        <div className="pf-control">
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.35rem 0.5rem", width: "100%" }}>{renderInput(param)}</div>
                        {isDouble && allowedUnits.length > 0 && (
                            <div style={{ marginTop: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                {allowedUnits.length > 1 && (
                                    <label style={{ fontWeight: 400, fontSize: "0.82rem" }}>
                                        Unit:{" "}
                                        <select
                                            value={selectedUnit ?? ""}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, [`__unit_${param.name}`]: e.target.value }))
                                            }
                                        >
                                            {allowedUnits.map((uid) => (
                                                <option key={uid} value={uid}>
                                                    {units.find(u => u.id === uid)?.name ?? uid}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <label style={{ fontWeight: 400, fontSize: "0.82rem" }}>
                                    <input
                                        type="checkbox"
                                        checked={formData[`__strict_${param.name}`] === true}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, [`__strict_${param.name}`]: e.target.checked }))
                                        }
                                    />
                                    {" "}Strict unit
                                </label>
                            </div>
                        )}
                        </div>
                    </div>
                );
            })}
            {!hideSubmit && <button type="submit" className="pf-submit">Submit</button>}
        </form>
    );
};

export default DynamicForm;